import { useCallback, useEffect } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { HELD_INTERMEDIATE_SURVEYS_PERSIST_KEY } from "../constants";

interface HeldIntermediateSurveysState {
  operationId: string | null;
  amounts: Record<string, number>;
  set: (operationId: string, code: string, value: number | null) => void;
  reset: () => void;
}

export const useHeldIntermediateSurveysStore = create<HeldIntermediateSurveysState>()(
  persist(
    (set) => ({
      operationId: null,
      amounts: {},
      set: (operationId, code, value) =>
        set((state) => {
          const next = { ...(state.operationId === operationId ? state.amounts : {}) };
          if (value === null) {
            delete next[code];
          } else {
            next[code] = value;
          }
          return {
            operationId: Object.keys(next).length > 0 ? operationId : null,
            amounts: next,
          };
        }),
      reset: () => set({ operationId: null, amounts: {} }),
    }),
    { name: HELD_INTERMEDIATE_SURVEYS_PERSIST_KEY },
  ),
);

const NONE: Record<string, number> = {};

export const useHeldIntermediateSurveys = (operationId: string) => {
  const storedOperationId = useHeldIntermediateSurveysStore((state) => state.operationId);
  const stored = useHeldIntermediateSurveysStore((state) => state.amounts);
  const setHeld = useHeldIntermediateSurveysStore((state) => state.set);
  const reset = useHeldIntermediateSurveysStore((state) => state.reset);
  useEffect(() => {
    if (storedOperationId !== null && storedOperationId !== operationId) {
      reset();
    }
  }, [storedOperationId, operationId, reset]);
  const set = useCallback(
    (code: string, value: number | null) => setHeld(operationId, code, value),
    [setHeld, operationId],
  );
  return {
    amounts: storedOperationId === operationId ? stored : NONE,
    set,
    reset,
  };
};
